import type {
  EmissionsLimitEvaluation,
  EmissionsLimitEvaluationInput,
  EmissionsLimitListItem,
  EmissionsLimitPeriod,
  EmissionsLimitPollutantSelector,
  EmissionsLimitWindow,
  EmissionsLimitNormalizedCalculation,
  PotentialEmissionsOutputSelection,
} from '../../types/emissions.ts';
import type { CasOrCategory, EmissionsTrackingFrequency, EmissionsUnitID, ProductID } from '../../types/db.ts';
import type { ISO8601DateOnly, ISO8601YearAndMonthOnly } from '../../../cross-app-shared/types/common.ts';
import { dateLocal, dateToLocaleDateString } from '../../../cross-app-shared/services/dates-and-durations.ts';
import { periodsProduce } from './emissions-dates.ts';
import { UnitsTracker } from '../formulas/units.ts';
import type {
  EmissionsQuestionsAndCalculationResultsForPeriod,
  EmissionsProductUsageItem,
} from '../reports/emissions-to-table.ts';
import {
  complianceMetricBreakingChanges,
  complianceMetricChangesSummary,
  complianceMetricDefinitionChanges,
} from './emissions-metric-snapshot.ts';
import type { FormulaOutputType } from '../../types/release-profiles.ts';
import { Num } from '../formulas/ast.ts';
import { potentialBasisPeriodHours } from './potential-basis-period.ts';

export function emissionsLimitValidationMessage(limit: EmissionsLimitListItem): string | null {
  if (!limit.name.trim()) return 'A limit name is required.';
  if (limit.emissionsUnits.length === 0) return 'At least one emissions unit is required.';
  if (
    limit.chemicalGrouping !== 'each-chemical-multiple-limits' &&
    (!Number.isFinite(limit.limitValue) || limit.limitValue <= 0)
  ) {
    return 'The limit must be greater than zero.';
  }
  if (limit.pollutantSelector.type === 'explicit' && limit.pollutantSelector.casOrCategories.length === 0) {
    return 'At least one pollutant is required.';
  }
  // A potential limit measures one snapshot of the unit's current configuration rather than a run of tracked
  // periods, so the rules about how those periods line up do not apply to it:
  const isPotentialLimit = limit.emissionsBasis === 'potential';
  if (
    !isPotentialLimit &&
    limit.window.type === 'rolling-months' &&
    (!Number.isInteger(limit.window.months) || limit.window.months <= 0)
  ) {
    return 'A rolling window must have a positive number of months.';
  }
  if (
    !isPotentialLimit &&
    !emissionsSourceFrequenciesNest(limit.emissionsUnits.map((unit) => unit.trackingFrequency))
  ) {
    return emissionsSourceFrequenciesDoNotNestMessage;
  }
  if (
    limit.unitGrouping === 'combined-units' &&
    limit.windowReduction === 'maximum' &&
    !emissionsSourceFrequenciesMatch(limit.emissionsUnits.map((unit) => unit.trackingFrequency))
  ) {
    return emissionsCombinedMaximumFrequenciesMustMatchMessage;
  }
  if (limit.windowReduction === 'ratio-of-totals' && limit.releaseProfileID === null) {
    return emissionsRatioOfTotalsRequiresComplianceMetricMessage;
  }

  return null;
}

/** Returns the highest potential-emissions snapshot and the calculations active at that point in time. */
export function emissionsPotentialPeakSnapshot<
  Calculation extends Pick<EmissionsLimitNormalizedCalculation, 'value' | 'periodStart' | 'periodEnd'>,
>(
  emissions: Iterable<Calculation>,
): {
  observedValue: number;
  emissions: Calculation[];
} {
  const calculations: Calculation[] = Array.from(emissions);
  let observedValue = 0;
  let peakSnapshot: Calculation[] = [];

  for (const snapshotDate of new Set(calculations.map((calculation) => calculation.periodStart))) {
    const snapshot = calculations.filter(
      (calculation) => calculation.periodStart <= snapshotDate && snapshotDate <= calculation.periodEnd,
    );
    const snapshotValue = snapshot.reduce((total, calculation) => total + calculation.value, 0);
    if (snapshotValue > observedValue) {
      observedValue = snapshotValue;
      peakSnapshot = snapshot;
    }
  }
  return { observedValue, emissions: peakSnapshot };
}

export function emissionsEffectiveDateAlignsWithSources(
  effectiveStartDate: ISO8601DateOnly,
  unitsConfigured: EmissionsUnitID[],
  units: {
    emissionsUnitID: EmissionsUnitID;
    entriesStartAt: ISO8601DateOnly;
    trackingFrequency: EmissionsTrackingFrequency;
  }[],
): boolean {
  return units
    .filter((unit) => unitsConfigured.includes(unit.emissionsUnitID))
    .every((unit) => {
      if (effectiveStartDate < unit.entriesStartAt) {
        return false;
      }

      if (unit.trackingFrequency === 'daily') {
        return true;
      }

      for (const period of periodsProduce(unit.trackingFrequency, effectiveStartDate, effectiveStartDate)) {
        if (period.start === effectiveStartDate) {
          return true;
        }
      }

      return false;
    });
}

export const emissionsSourceFrequenciesDoNotNestMessage =
  'Weekly-tracked sources cannot be combined with monthly or less frequent sources, because a week can straddle a ' +
  'month, quarter, half-year or year boundary.';

export const emissionsCombinedMaximumFrequenciesMustMatchMessage =
  'A combined limit using the maximum period value requires every source to use the same tracking frequency.';

export const emissionsRatioOfTotalsRequiresComplianceMetricMessage =
  'A ratio-of-totals limit requires a compliance metric.';

export function emissionsSourceFrequenciesMatch(frequencies: EmissionsTrackingFrequency[]): boolean {
  return frequencies.length < 2 || frequencies.every((frequency) => frequency === frequencies[0]);
}

/**
 * A limit's periods are produced from the coarsest tracking frequency of its sources (see emissionsLimitPeriods), and
 * emissions are only attributed to a period that fully contains their own tracking period. Every frequency nests
 * cleanly inside the coarser ones except weekly, which can straddle a month, quarter, half-year or year boundary — so
 * mixing a weekly source with a monthly-or-coarser one would drop the straddling weeks.
 */
export function emissionsSourceFrequenciesNest(frequencies: EmissionsTrackingFrequency[]): boolean {
  return (
    !frequencies.includes('weekly') || frequencies.every((frequency) => frequency === 'weekly' || frequency === 'daily')
  );
}

export const trackingFrequencyRank: Record<EmissionsTrackingFrequency, number> = {
  daily: 0,
  weekly: 1,
  monthly: 2,
  quarterly: 3,
  semiannually: 4,
  annually: 5,
};

export function emissionsLimitWindowLabel(window: EmissionsLimitWindow): string {
  if (window.type === 'rolling-months') {
    return `Rolling ${window.months} months`;
  }

  return {
    'calendar-year': 'Calendar year',
    'calendar-month': 'Calendar month',
    'tracked-period': 'Tracked period',
    total: 'Total tracked emissions',
  }[window.type];
}

export const emissionsLimitStatusToLabel: Record<EmissionsLimitEvaluation['status'], string> = {
  exceeded: 'Exceeding',
  compliant: 'Compliant',
  provisional: 'Provisional',
  'not-applicable': 'Not applicable',
  'configuration-error': 'Needs setup',
};

type LimitPeriodGenerator = Generator<EmissionsLimitPeriod, void, void>;

function* clampStart(effectiveStartDate: ISO8601DateOnly, generator: LimitPeriodGenerator): LimitPeriodGenerator {
  const first = generator.next();
  if (first.value) {
    const period = first.value;
    if (period.start < effectiveStartDate) {
      yield {
        ...period,
        start: effectiveStartDate,
        friendly: `${dateToLocaleDateString(effectiveStartDate)} - ${dateToLocaleDateString(period.end)}`,
      };
    } else {
      yield period;
    }
  }

  yield* generator;
}

function largestFrequency(limit: EmissionsLimitListItem): EmissionsTrackingFrequency {
  return (
    limit.emissionsUnits
      .map((u) => u.trackingFrequency)
      .sort((a, b) => trackingFrequencyRank[b] - trackingFrequencyRank[a])[0] ?? 'annually'
  );
}

export function* emissionsLimitPeriods(limit: EmissionsLimitListItem, end: ISO8601DateOnly): LimitPeriodGenerator {
  if (limit.effectiveStartDate > end) {
    return;
  }

  // A potential limit caps what the unit could emit running as it is configured right now, so it has exactly one
  // period: this moment. Its configured window says nothing about it, and there is no history to walk.
  if (limit.emissionsBasis === 'potential') {
    yield { start: end, end, friendly: 'Current PTE' };
    return;
  }

  if (limit.window.type === 'tracked-period') {
    yield* clampStart(limit.effectiveStartDate, periodsProduce(largestFrequency(limit), limit.effectiveStartDate, end));
  } else if (limit.window.type === 'calendar-month') {
    yield* clampStart(limit.effectiveStartDate, periodsProduce('monthly', limit.effectiveStartDate, end));
  } else if (limit.window.type === 'calendar-year') {
    yield* clampStart(limit.effectiveStartDate, periodsProduce('annually', limit.effectiveStartDate, end));
  } else if (limit.window.type === 'total') {
    // In emissionsLimitEvaluate we test emission unit periods for full overlap, so we need to pull the end date of
    // the final relevant period to capture any in-progress periods:
    const lastPeriodEnd = periodsProduce(largestFrequency(limit), end, end).next().value?.end ?? end;
    yield { start: limit.effectiveStartDate, end: lastPeriodEnd, friendly: 'Total' };
  } else if (limit.window.type === 'rolling-months') {
    for (const month of clampStart(
      limit.effectiveStartDate,
      periodsProduce('monthly', limit.effectiveStartDate, end),
    )) {
      const startObj = new Date(month.start);
      startObj.setUTCMonth(startObj.getUTCMonth() - (limit.window.months - 1));
      const rollingStart = startObj.toISOString().slice(0, 10) as ISO8601DateOnly;
      const start = rollingStart < limit.effectiveStartDate ? limit.effectiveStartDate : rollingStart;
      yield {
        start,
        end: month.end,
        friendly: `${dateToLocaleDateString(start)} - ${dateToLocaleDateString(month.end)}`,
      };
    }
  } else {
    const unexpected: never = limit.window;
    console.error(`Unexpected limit window:`, unexpected);
  }
}

/**
 * Which formula outputs a limit measures itself against. A limit built on a compliance metric reads that metric's
 * outputs; every other limit reads the unit's actual emissions. Note that emissionsLimitNormalizeCalculation returns
 * every line a unit produced — potential emissions and other profiles' compliance metrics included — so anything
 * displaying a limit's inputs has to narrow them the same way emissionsLimitEvaluate does.
 */
export function emissionsLimitAcceptedOutputTypes(
  limit: Pick<EmissionsLimitListItem, 'windowReduction' | 'releaseProfileID' | 'emissionsBasis'>,
): Set<FormulaOutputType> {
  if (limit.windowReduction === 'ratio-of-totals') {
    return new Set(['complianceNumerator', 'complianceDenominator']);
  }

  return new Set([limit.releaseProfileID ? 'complianceNumerator' : limit.emissionsBasis]);
}

/** Whether a single calculated line is one of the values this limit is measured against. */
export function emissionsLimitMeasuresCalculation(
  limit: Pick<EmissionsLimitListItem, 'windowReduction' | 'releaseProfileID' | 'emissionsBasis'>,
  calc: Pick<EmissionsLimitNormalizedCalculation, 'releaseProfileID' | 'formulaOutputType'>,
  acceptedOutputTypes: Set<FormulaOutputType> = emissionsLimitAcceptedOutputTypes(limit),
): boolean {
  if (limit.releaseProfileID && calc.releaseProfileID !== limit.releaseProfileID) {
    return false;
  }

  return acceptedOutputTypes.has(calc.formulaOutputType);
}

/**
 * Whether a line of a ratio's denominator belongs to every group it could divide rather than to a group of its own. A
 * ratio's denominator is often reported against no chemical at all — a throughput, a production total, the fuel burned
 * — and sometimes against no product either, so under a per-chemical or per-product grouping it has to be shared with
 * each group measured against it, or every group would hold half of the ratio and none of them could compute it. A
 * denominator that does carry a chemical or a product is only shared with the groups holding that same one.
 */
function emissionsLimitDenominatorIsShared(
  limit: Pick<EmissionsLimitListItem, 'chemicalGrouping' | 'productGrouping' | 'windowReduction'>,
  calc: Pick<EmissionsLimitNormalizedCalculation, 'casOrCategory' | 'product' | 'formulaOutputType'>,
): boolean {
  return (
    limit.windowReduction === 'ratio-of-totals' &&
    calc.formulaOutputType === 'complianceDenominator' &&
    ((limit.chemicalGrouping !== 'combined-chemicals' && calc.casOrCategory === null) ||
      (limit.productGrouping === 'each-product' && calc.product === null))
  );
}

export function emissionsLimitNormalizeCalculation(
  calcs: EmissionsQuestionsAndCalculationResultsForPeriod[],
  productUsageForDisplayedPeriods: EmissionsProductUsageItem[],
): EmissionsLimitNormalizedCalculation[] {
  const result: EmissionsLimitNormalizedCalculation[] = [];

  for (const calc of calcs) {
    const contextProducts = productUsageForDisplayedPeriods
      .filter(
        (usage) =>
          usage.period.period === calc.period.period &&
          usage.scope.type === 'unitIntervalAnswers' &&
          usage.scope.emissionsUnitID === calc.unit.emissionsUnitID,
      )
      .map((usage) => ({ productName: usage.product.name, amount: usage.value, units: usage.units }));

    for (const line of calc.calculationLines) {
      result.push({
        emissionsUnitID: calc.unit.emissionsUnitID,
        emissionsUnitName: calc.unit.unit.name,
        casOrCategory: line.casOrCategoryMapped,
        product: line.product
          ? {
              productID: line.product.productID,
              productName: line.product.productName,
            }
          : null,
        releaseProfileID: line.releaseProfileID,
        formulaOutputType: line.formulaOutputType,
        releaseProfileFormulaID: line.releaseProfileFormulaID,
        releaseProfileOutputID: line.releaseProfileOutputID,
        potentialBasisPeriod: line.potentialBasisPeriod,
        // Emit 0 so we don't trash our sums, the warning TW1116 will have been emitted by releases-calculate.ts and
        // ensures this non-finite result doesn't get missed:
        value: Number.isFinite(line.computationResult) ? line.computationResult : 0,
        units: line.computationUnits,
        periodStart: calc.period.start,
        periodEnd: calc.period.end,
        parentPeriodStart: calc.period.type === 'subPeriod' ? calc.period.parentPeriod.start : calc.period.start,
        parentPeriodEnd: calc.period.type === 'subPeriod' ? calc.period.parentPeriod.end : calc.period.end,
        isQuestionnaireComplete: calc.questionsAnswered === calc.questionsTotal,
        // Most common cause of warnings is missing data entry:
        hasCalculationWarnings: calc.warnings.length > 0,
        drilldown: {
          unit: calc.unit,
          period: calc.period,
          line,
          contextProducts,
          // Only a potential-basis limit knows the basis it caps against, so it fills this in when it hands its
          // measured lines to a drilldown:
          potentialNormalization: null,
        },
      });
    }
  }

  return result;
}

function selectorMatches(
  selector: EmissionsLimitPollutantSelector,
  casOrCategory: CasOrCategory,
  isHap: boolean | undefined,
): boolean {
  if (selector.type === 'explicit') return selector.casOrCategories.includes(casOrCategory);
  return selector.classification === 'all' || isHap === true;
}

function* monthsSpanned(start: ISO8601DateOnly, end: ISO8601DateOnly): Generator<ISO8601YearAndMonthOnly, void, void> {
  const last = end.slice(0, 7);
  let year = Number(start.slice(0, 4));
  let month = Number(start.slice(5, 7));
  while (true) {
    const period = `${year}-${String(month).padStart(2, '0')}` as ISO8601YearAndMonthOnly;
    if (period > last) {
      return;
    }
    yield period;
    if (month === 12) {
      year++;
      month = 1;
    } else {
      month++;
    }
  }
}

function convertAndSum(
  accum: Num | null,
  calc: EmissionsLimitNormalizedCalculation,
  failures: { calc: EmissionsLimitNormalizedCalculation; wantedToConvertTo: string }[],
): Num | null {
  const calcUnits = UnitsTracker.fromParsedUnitString(calc.units);
  if (!accum) {
    return new Num(calc.value, calcUnits);
  }

  const converted = calcUnits.convertTo(accum.units, calc.value);
  if (typeof converted === 'number') {
    return new Num(accum.value + converted, accum.units);
  } else {
    failures.push({ calc, wantedToConvertTo: accum.units.toString() });
    return accum;
  }
}

export function* emissionsLimitEvaluate(
  input: EmissionsLimitEvaluationInput,
  today = dateLocal(),
): Generator<EmissionsLimitEvaluation, void, void> {
  const validationError = emissionsLimitValidationMessage(input.limit);
  const errorPeriod: EmissionsLimitPeriod =
    today > input.limit.effectiveStartDate
      ? {
          start: input.limit.effectiveStartDate,
          end: today,
          friendly: dateToLocaleDateString(input.limit.effectiveStartDate) + ' - ' + dateToLocaleDateString(today),
        }
      : {
          start: today,
          end: input.limit.effectiveStartDate,
          friendly: dateToLocaleDateString(today) + ' - ' + dateToLocaleDateString(input.limit.effectiveStartDate),
        };

  const { pollutantSelector, chemicalGrouping, unitGrouping, productGrouping, limitUnit, windowReduction } =
    input.limit;
  const isPotential = input.limit.emissionsBasis === 'potential';

  if (validationError) {
    yield {
      status: 'configuration-error',
      message: validationError,
      period: errorPeriod,
      casOrCategory: null,
      productID: null,
    };
    return;
  }

  // A metric-based limit measures a definition an admin can edit at any time. Where that edit changed what the limit
  // means, the honest verdict is that it needs review, not a number computed against a definition its author never
  // approved. Changes that only move the value are left to the UI to flag, so the limit keeps reporting.
  if (input.releaseProfilesAndVariables) {
    const breaking = complianceMetricBreakingChanges(
      complianceMetricDefinitionChanges(input.limit, input.releaseProfilesAndVariables),
    );
    if (breaking.length > 0) {
      yield {
        status: 'configuration-error',
        message: complianceMetricChangesSummary(
          breaking,
          input.limit.complianceMetricSnapshot?.labels.metricName ?? 'this limit',
        ),
        period: errorPeriod,
        casOrCategory: null,
        productID: null,
      };
      return;
    }
  }

  const limitUnitsTracker = UnitsTracker.fromParsedUnitString(input.limit.limitUnit);
  if (limitUnitsTracker.isUnknown()) {
    yield {
      status: 'configuration-error',
      message: `The limit is configured with units ${input.limit.limitUnit} which this system does not understand.`,
      period: errorPeriod,
      casOrCategory: null,
      productID: null,
    };
    return;
  }

  if (input.limit.effectiveStartDate > today) {
    yield {
      status: 'not-applicable',
      casOrCategories: [],
      emissionsUnitIds: [],
      products: [],
      observedValue: 0,
      limitValue: input.limit.limitValue,
      unit: limitUnit,
      percentOfLimit: 0,
      message: 'This limit has not yet gone into effect.',
      period: errorPeriod,
      calculations: [],
    };
    return;
  }

  if (input.limit.chemicalGrouping === 'each-chemical-multiple-limits' && input.limit.pollutantLimits.length === 0) {
    yield {
      status: 'configuration-error',
      message: `The limit is configured with unique limits per chemical, but none are configured.`,
      period: errorPeriod,
      casOrCategory: null,
      productID: null,
    };
    return;
  }

  if (
    input.limit.chemicalGrouping === 'each-chemical-multiple-limits' &&
    input.limit.pollutantSelector.type === 'explicit' &&
    !input.limit.pollutantSelector.casOrCategories.every((c) =>
      input.limit.pollutantLimits.some((l) => l.casOrCategory === c),
    )
  ) {
    yield {
      status: 'configuration-error',
      message: `The limit is configured with unique limits per chemical, but some configured chemicals do not have a limit.`,
      period: errorPeriod,
      casOrCategory: null,
      productID: null,
    };
    return;
  }

  const scopedUnitIDs = new Set(input.limit.emissionsUnits.map((u) => u.emissionsUnitID));
  const chemicalsUsing = new Set<CasOrCategory>();
  const productsUsing = new Map<ProductID, string>();
  const pollutantLimitChemicals = new Set<CasOrCategory>(input.limit.pollutantLimits.map((p) => p.casOrCategory));

  if (pollutantSelector.type === 'explicit') {
    for (const casOrCategory of pollutantSelector.casOrCategories) {
      chemicalsUsing.add(casOrCategory);
    }
  }

  // Since limits and units may be changed independently in various ways, we want to ensure we never end up in a
  // situation where our calculation periods are accidentally misaligned. We'll assume all calculations are unused
  // until we see them used:
  const calcsUnused = new Set<EmissionsLimitNormalizedCalculation>();
  // We group calculations by month in order to avoid iterating all calculations for each period:
  const calcsByMonth = new Map<ISO8601YearAndMonthOnly, EmissionsLimitNormalizedCalculation[]>();

  const acceptedOutputTypes = emissionsLimitAcceptedOutputTypes(input.limit);

  // A potential limit measures specific PTE outputs, each reporting over whatever basis period its own formula
  // declares, against a cap stated over the limit's saved basis period. Narrow the calculations to the saved
  // selections and rescale them onto that basis here, so everything below can group and reduce them like any other.
  let emissions: readonly EmissionsLimitNormalizedCalculation[] = input.emissions;
  if (input.limit.emissionsBasis === 'potential') {
    const capBasisPeriod = input.limit.potentialBasisPeriod;
    if (!capBasisPeriod) {
      yield {
        status: 'configuration-error',
        message:
          'This potential-emissions limit is missing its saved cap basis. Review and save the limit to derive it from the selected outputs.',
        period: errorPeriod,
        casOrCategory: null,
        productID: null,
      };
      return;
    }
    if (input.limit.potentialOutputSelections.length === 0) {
      yield {
        status: 'configuration-error',
        message: 'This potential-emissions limit has no selected PTE outputs. Review and save the limit.',
        period: errorPeriod,
        casOrCategory: null,
        productID: null,
      };
      return;
    }

    const selectionKey = (selection: PotentialEmissionsOutputSelection) =>
      `${selection.emissionsUnitID}:${selection.releaseProfileID}:${selection.formulaID}:${selection.outputID}`;
    // A unit that rolls its PTE into its actuals reports every period as that same potential output restamped as an
    // actual line, carrying the formula and output it was computed from but no basis period. Only the potential lines
    // are what this limit measures, so the output type is part of the identity: without it those restamped lines
    // match a saved selection and then fail the basis-period check below.
    const calcKey = (calc: EmissionsLimitNormalizedCalculation) =>
      calc.formulaOutputType === 'potential' &&
      calc.releaseProfileID &&
      calc.releaseProfileFormulaID &&
      calc.releaseProfileOutputID
        ? `${calc.emissionsUnitID}:${calc.releaseProfileID}:${calc.releaseProfileFormulaID}:${calc.releaseProfileOutputID}`
        : null;

    const selectedUnitIDs = new Set(input.limit.potentialOutputSelections.map((s) => s.emissionsUnitID));
    const missingUnits = Array.from(scopedUnitIDs).filter((emissionsUnitID) => !selectedUnitIDs.has(emissionsUnitID));
    if (missingUnits.length > 0) {
      yield {
        status: 'configuration-error',
        message: `No PTE output is selected for ${missingUnits.join(', ')}.`,
        period: errorPeriod,
        casOrCategory: null,
        productID: null,
      };
      return;
    }

    const selectedKeys = new Set(input.limit.potentialOutputSelections.map(selectionKey));
    const matched = input.emissions.filter((calc) => {
      const key = calcKey(calc);
      return key !== null && selectedKeys.has(key);
    });
    if (matched.some((calc) => !calc.potentialBasisPeriod)) {
      yield {
        status: 'configuration-error',
        message: 'Selected potential-emissions outputs must declare a basis period.',
        period: errorPeriod,
        casOrCategory: null,
        productID: null,
      };
      return;
    }
    const matchedKeys = new Set(matched.map(calcKey));
    if (input.limit.potentialOutputSelections.some((selection) => !matchedKeys.has(selectionKey(selection)))) {
      yield {
        status: 'configuration-error',
        message: 'One or more selected PTE outputs are unavailable for the selected pollutants.',
        period: errorPeriod,
        casOrCategory: null,
        productID: null,
      };
      return;
    }

    const capBasisHours = potentialBasisPeriodHours(capBasisPeriod);
    emissions = matched.map((calc) => ({
      ...calc,
      value: (calc.value * capBasisHours) / potentialBasisPeriodHours(calc.potentialBasisPeriod!),
      potentialBasisPeriod: capBasisPeriod,
    }));
  }

  for (const calc of emissions) {
    if (!scopedUnitIDs.has(calc.emissionsUnitID)) {
      continue;
    }

    // If the unused calculation is before the start date, it is intentionally unused:
    if (calc.periodEnd < input.limit.effectiveStartDate) {
      continue;
    }

    // A denominator belonging to this limit's own metric has nowhere to go outside a ratio, and quietly dropping it
    // below would leave the limit measuring only half of what the metric defines:
    if (
      input.limit.releaseProfileID &&
      calc.releaseProfileID === input.limit.releaseProfileID &&
      windowReduction !== 'ratio-of-totals' &&
      calc.formulaOutputType === 'complianceDenominator'
    ) {
      yield {
        status: 'configuration-error',
        message: `Compliance denominator formulas can only be used with ratio-of-totals limits.`,
        period: errorPeriod,
        casOrCategory: calc.casOrCategory,
        productID: calc.product?.productID ?? null,
      };
      return;
    }

    if (!emissionsLimitMeasuresCalculation(input.limit, calc, acceptedOutputTypes)) {
      continue;
    }

    // If the unit computed more chemicals than are in the limit, we can safely skip those calculations:
    if (
      calc.casOrCategory &&
      !selectorMatches(pollutantSelector, calc.casOrCategory, input.chemicalsByCasOrCategory[calc.casOrCategory]?.isHap)
    ) {
      continue;
    }

    // If no per-chemical limit was configured, raise an error:
    if (
      chemicalGrouping === 'each-chemical-multiple-limits' &&
      calc.casOrCategory &&
      !pollutantLimitChemicals.has(calc.casOrCategory)
    ) {
      yield {
        status: 'configuration-error',
        message: `Missing per-chemical limit for ${calc.casOrCategory}`,
        period: errorPeriod,
        casOrCategory: calc.casOrCategory,
        productID: calc.product?.productID ?? null,
      };
      return;
    }

    // It is expected that some formulas may emit values of the incompatible units (i.e. they may be computing
    // ratios or volumes which are re-used in other formulas that compute mass). So we just skip those:
    if (
      windowReduction !== 'ratio-of-totals' &&
      typeof UnitsTracker.fromParsedUnitString(calc.units).convertTo(limitUnitsTracker, calc.value) !== 'number'
    ) {
      continue;
    }

    calcsUnused.add(calc);
    if (calc.casOrCategory) {
      chemicalsUsing.add(calc.casOrCategory);
    }
    if (calc.product) {
      productsUsing.set(calc.product.productID, calc.product.productName);
    }

    for (const month of monthsSpanned(calc.periodStart, calc.periodEnd)) {
      if (calcsByMonth.has(month)) {
        calcsByMonth.get(month)!.push(calc);
      } else {
        calcsByMonth.set(month, [calc]);
      }
    }
  }

  function* calcsContainedInPeriod(
    period: EmissionsLimitPeriod,
  ): Generator<EmissionsLimitNormalizedCalculation, void, void> {
    const calcsEmitted = new Set<EmissionsLimitNormalizedCalculation>();
    for (const month of monthsSpanned(period.start, period.end)) {
      for (const calc of calcsByMonth.get(month) ?? []) {
        if (period.start <= calc.periodStart && period.end >= calc.periodEnd && !calcsEmitted.has(calc)) {
          yield calc;
          calcsEmitted.add(calc);
        }
      }
    }
  }

  type UnitKey = EmissionsUnitID | 'combined-units';
  type ChemicalKey = CasOrCategory | 'combined-chemicals' | null;
  type ProductKey = ProductID | 'combined-products' | null;
  const unitKeyOf = (calc: EmissionsLimitNormalizedCalculation): UnitKey =>
    unitGrouping === 'combined-units' ? 'combined-units' : calc.emissionsUnitID;
  const chemicalKeyOf = (calc: EmissionsLimitNormalizedCalculation): ChemicalKey =>
    chemicalGrouping === 'combined-chemicals' ? 'combined-chemicals' : calc.casOrCategory;
  const productKeyOf = (calc: EmissionsLimitNormalizedCalculation): ProductKey =>
    productGrouping === 'combined-products' ? 'combined-products' : (calc.product?.productID ?? null);

  // Every group a period is expected to report on, so that one the period calculated nothing for still reports as not
  // applicable rather than silently disappearing:
  const unitKeysExpected: UnitKey[] =
    unitGrouping === 'combined-units' ? ['combined-units'] : Array.from(scopedUnitIDs);
  const chemicalKeysExpected: ChemicalKey[] =
    chemicalGrouping === 'combined-chemicals' ? ['combined-chemicals'] : Array.from(chemicalsUsing);
  const productKeysExpected: ProductKey[] =
    productGrouping === 'combined-products'
      ? ['combined-products']
      : productsUsing.size > 0
        ? Array.from(productsUsing.keys())
        : // A metric that never depends on products still has to report on something:
          [null];

  const productsOfKey = (productKey: ProductKey): { productID: ProductID; productName: string }[] =>
    productKey === 'combined-products'
      ? Array.from(productsUsing, ([productID, productName]) => ({ productID, productName }))
      : productKey === null
        ? []
        : [{ productID: productKey, productName: productsUsing.get(productKey) ?? String(productKey) }];

  let sawCalculationEmitted = false;
  for (const period of emissionsLimitPeriods(input.limit, today)) {
    type CalcsKey = `${UnitKey}-${ChemicalKey}-${ProductKey}`;
    const calcsByGroup = new Map<
      CalcsKey,
      {
        emissionsUnitID: UnitKey;
        casOrCategory: ChemicalKey;
        productID: ProductKey;
        calcs: EmissionsLimitNormalizedCalculation[];
      }
    >();

    for (const emissionsUnitID of unitKeysExpected) {
      for (const casOrCategory of chemicalKeysExpected) {
        for (const productID of productKeysExpected) {
          calcsByGroup.set(`${emissionsUnitID}-${casOrCategory}-${productID}`, {
            emissionsUnitID,
            casOrCategory,
            productID,
            calcs: [],
          });
        }
      }
    }

    // Held back from the grouping below and handed to every group that measured something, rather than to the one
    // group their own missing chemical or product would key on — see emissionsLimitDenominatorIsShared:
    const sharedDenominators: EmissionsLimitNormalizedCalculation[] = [];

    for (const calc of calcsContainedInPeriod(period)) {
      if (emissionsLimitDenominatorIsShared(input.limit, calc)) {
        sharedDenominators.push(calc);
        continue;
      }

      calcsUnused.delete(calc);

      const emissionsUnitID = unitKeyOf(calc);
      const casOrCategory = chemicalKeyOf(calc);
      const productID = productKeyOf(calc);
      const key: CalcsKey = `${emissionsUnitID}-${casOrCategory}-${productID}`;
      if (calcsByGroup.has(key)) {
        calcsByGroup.get(key)!.calcs.push(calc);
      } else {
        calcsByGroup.set(key, { emissionsUnitID, casOrCategory, productID, calcs: [calc] });
      }
    }

    // Move all shared denominators into the more specific calculation groups:
    for (const denominator of sharedDenominators) {
      const emissionsUnitID = unitKeyOf(denominator);
      for (const group of calcsByGroup.values()) {
        if (
          group.emissionsUnitID === emissionsUnitID &&
          (denominator.casOrCategory === null || group.casOrCategory === chemicalKeyOf(denominator)) &&
          (denominator.product === null || group.productID === productKeyOf(denominator)) &&
          // A group the metric calculated nothing for is left empty so it reports the period as not applicable, rather
          // than being handed a denominator it has no numerator to divide:
          group.calcs.length > 0
        ) {
          group.calcs.push(denominator);
          calcsUnused.delete(denominator);
        }
      }
    }

    for (const group of calcsByGroup.values()) {
      const groupProductID = group.productID === 'combined-products' ? null : group.productID;
      let limitValue: number;
      if (chemicalGrouping === 'each-chemical-multiple-limits') {
        limitValue = input.limit.pollutantLimits.find((l) => l.casOrCategory === group.casOrCategory)?.limitValue ?? 0;
        if (!limitValue) {
          yield {
            status: 'configuration-error',
            message: `The limit is configured with unique limits per chemical, but found a missing or 0-valued limit for ${group.casOrCategory}.`,
            period,
            casOrCategory: group.casOrCategory === 'combined-chemicals' ? null : group.casOrCategory,
            productID: groupProductID,
          };
          continue;
        }
      } else {
        limitValue = input.limit.limitValue;
      }

      const notApplicable = (
        message: string,
        calculations: EmissionsLimitNormalizedCalculation[],
      ): EmissionsLimitEvaluation => ({
        status: 'not-applicable',
        casOrCategories:
          group.casOrCategory === 'combined-chemicals'
            ? Array.from(chemicalsUsing)
            : group.casOrCategory
              ? [group.casOrCategory]
              : [],
        emissionsUnitIds:
          group.emissionsUnitID === 'combined-units' ? Array.from(scopedUnitIDs) : [group.emissionsUnitID],
        products: productsOfKey(group.productID),
        observedValue: 0,
        limitValue,
        unit: limitUnit,
        percentOfLimit: 0,
        message,
        period,
        calculations,
      });

      if (group.calcs.length === 0) {
        yield notApplicable('No calculation was performed for this pollutant during this period.', []);
      } else {
        const hasCalculationWarnings = group.calcs.some((calc) => calc.hasCalculationWarnings);
        const isQuestionnaireComplete = group.calcs.every((calc) => calc.isQuestionnaireComplete);
        let observedValue: number;
        if (windowReduction === 'ratio-of-totals') {
          let numerator: Num | null = null;
          let denominator: Num | null = null;
          const conversionFailures: { calc: EmissionsLimitNormalizedCalculation; wantedToConvertTo: string }[] = [];

          for (const calc of group.calcs) {
            if (calc.formulaOutputType === 'complianceNumerator') {
              numerator = convertAndSum(numerator, calc, conversionFailures);
            } else if (calc.formulaOutputType === 'complianceDenominator') {
              denominator = convertAndSum(denominator, calc, conversionFailures);
            }
          }

          if (conversionFailures.length > 0) {
            yield {
              status: 'configuration-error',
              message: `When computing ratio of totals, we failed to convert ${conversionFailures[0].calc.value} ${conversionFailures[0].calc.units} to ${conversionFailures[0].wantedToConvertTo}.`,
              period,
              casOrCategory: group.casOrCategory === 'combined-chemicals' ? null : group.casOrCategory,
              productID: groupProductID,
            };
            continue;
          }

          if (!numerator) {
            yield {
              status: 'configuration-error',
              message: `When computing ratio of totals, did not find any numerator.`,
              period,
              casOrCategory: group.casOrCategory === 'combined-chemicals' ? null : group.casOrCategory,
              productID: groupProductID,
            };
            continue;
          }

          if (!denominator) {
            // A metric with no denominator measures its numerator alone, which is only a total if the numerator's
            // lines can be added up. Lines that are already ratios — a content in lb/gal, a percentage — can only be
            // compared one at a time, so a group holding several of them has no meaningful total:
            const numerators = group.calcs.filter((calc) => calc.formulaOutputType === 'complianceNumerator');
            const numeratorIsRatio = numerators.some((calc) => UnitsTracker.fromParsedUnitString(calc.units).isRatio());
            const spans = (keyOf: (calc: EmissionsLimitNormalizedCalculation) => unknown) =>
              new Set(numerators.map(keyOf)).size > 1;
            if (
              numeratorIsRatio &&
              (spans((calc) => `${calc.periodStart}-${calc.periodEnd}`) ||
                spans((calc) => calc.emissionsUnitID) ||
                spans((calc) => calc.product?.productID ?? null))
            ) {
              yield {
                status: 'configuration-error',
                message:
                  `The compliance metric reports a ratio (${numerator.units.toString()}) with no denominator, so it ` +
                  'cannot be totalled across several tracking periods, sources, or products. Give the metric a ' +
                  "denominator output, or narrow the limit's window and grouping so each verdict measures a single value.",
                period,
                casOrCategory: group.casOrCategory === 'combined-chemicals' ? null : group.casOrCategory,
                productID: groupProductID,
              };
              continue;
            }

            denominator = new Num(1, UnitsTracker.unitless());
          }

          if (denominator.value === 0) {
            if (numerator.value === 0 && isQuestionnaireComplete && !hasCalculationWarnings) {
              yield notApplicable("The ratio's numerator and denominator both totalled zero.", group.calcs);
              sawCalculationEmitted = true;
            } else {
              yield {
                status: 'configuration-error',
                message:
                  numerator.value === 0
                    ? 'When computing ratio of totals, the numerator and denominator both totalled zero, but one or more inputs are incomplete or have calculation warnings.'
                    : `When computing ratio of totals, the denominator totalled zero while the numerator totalled ${numerator.value} ${numerator.units.toString()}.`,
                period,
                casOrCategory: group.casOrCategory === 'combined-chemicals' ? null : group.casOrCategory,
                productID: groupProductID,
              };
            }
            continue;
          }

          const divided = numerator.divide(denominator);
          const converted = divided.units.convertTo(limitUnitsTracker, divided.value);
          if (typeof converted !== 'number') {
            yield {
              status: 'configuration-error',
              message: `When computing ratio of totals, we computed ${divided.value} ${divided.units.toString()} which we cannot convert to ${limitUnitsTracker.toString()}.`,
              period,
              casOrCategory: group.casOrCategory === 'combined-chemicals' ? null : group.casOrCategory,
              productID: groupProductID,
            };
            continue;
          }

          observedValue = converted;
        } else {
          const convertedCalcs = group.calcs.map((emission) => ({
            emission,
            value: UnitsTracker.fromParsedUnitString(emission.units).convertTo(
              limitUnitsTracker,
              emission.value,
            ) as number, // When we build our master list of calculations above, we ensure only compatible units are included.
          }));

          if (windowReduction === 'sum') {
            observedValue = convertedCalcs.reduce((accum, { value: convertedValue }) => {
              return accum + convertedValue;
            }, 0);
          } else if (windowReduction === 'maximum') {
            // For maximum, we sum all calculations within each tracking period this limit contains, and then pick the worst period:
            const totalsByTrackingPeriod = new Map<`${ISO8601DateOnly}-${ISO8601DateOnly}`, number>();
            for (const { emission, value } of convertedCalcs) {
              const trackingPeriod = `${emission.parentPeriodStart}-${emission.parentPeriodEnd}` as const;
              totalsByTrackingPeriod.set(trackingPeriod, (totalsByTrackingPeriod.get(trackingPeriod) ?? 0) + value);
            }
            observedValue = Array.from(totalsByTrackingPeriod.values()).reduce(
              (maximum, periodTotal) => Math.max(maximum, periodTotal),
              0,
            );
          } else {
            const unexpected: never = windowReduction;
            console.error(`Unexpected windowReduction value: ${unexpected}`);
            observedValue = 0;
          }
        }
        const percentOfLimit = observedValue / limitValue;
        const status =
          percentOfLimit > 1
            ? 'exceeded'
            : !isQuestionnaireComplete || hasCalculationWarnings
              ? 'provisional'
              : 'compliant';
        yield {
          status,
          casOrCategories: Array.from(
            new Set(group.calcs.map((c) => c.casOrCategory).filter((c): c is CasOrCategory => !!c)),
          ),
          emissionsUnitIds: Array.from(new Set(group.calcs.map((c) => c.emissionsUnitID))),
          products: Array.from(
            new Map(
              group.calcs.flatMap((c) =>
                c.product
                  ? [
                      [
                        c.product.productID,
                        { productID: c.product.productID, productName: c.product.productName },
                      ] as const,
                    ]
                  : [],
              ),
            ).values(),
          ),
          observedValue,
          limitValue,
          unit: limitUnit,
          percentOfLimit,
          message:
            status === 'provisional'
              ? isPotential
                ? 'One or more PTE calculations have warnings.'
                : 'One or more tracking periods are incomplete.'
              : status === 'exceeded'
                ? isPotential
                  ? 'Potential emissions exceed the limit.'
                  : 'Actual emissions exceed the limit.'
                : null,
          period,
          calculations: group.calcs,
        };
      }
      sawCalculationEmitted = true;
    }

    if (calcsByGroup.size === 0) {
      yield {
        status: 'provisional',
        casOrCategories: [],
        emissionsUnitIds: [],
        products: [],
        observedValue: 0,
        limitValue: input.limit.limitValue,
        unit: limitUnit,
        percentOfLimit: 0,
        message: `The period ${period.friendly} is incomplete.`,
        period,
        calculations: [],
      };
      // We don't set sawCalculationEmitted because no calculation occurred, and so both 'provisional' and
      // 'configuration-error' rows are desired so we can show the empty periods _and_ flag that this is probably a
      // config problem.
    }
  }

  if (!sawCalculationEmitted) {
    yield {
      status: 'configuration-error',
      message: 'No chemicals were ever found while computing this limit.',
      period: errorPeriod,
      casOrCategory: null,
      productID: null,
    };
  }

  for (const unused of calcsUnused) {
    yield {
      status: 'configuration-error',
      message: `${unused.emissionsUnitName} (${unused.emissionsUnitID}) calculated ${unused.value} ${unused.units} of ${unused.casOrCategory} on period ${unused.periodStart} through ${unused.periodEnd}, but this did not contribute to the limit. This is most likely due to a configuration error.`,
      period: errorPeriod,
      casOrCategory: null,
      productID: null,
    };
  }
}
